import { useState, useEffect, useMemo, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { Transition } from "react-transition-group";
import ReactDOM from "react-dom";
import { hideModal } from "../../../store/actions/modal";
import Checkbox from "../../../components/FormikForm/Checkbox";
import Input from "../../../components/FormikForm/Input";
import { Formik } from "formik";
import {
  schema,
  initialValues,
} from "../../../constants/forms/ask-for-user-information";

import {
  Modal,
  ModalDialog,
  ModalContent,
  ModalBody,
  Title,
  SubTitle,
  ImageWrapper,
  Image,
  FormWrapper,
  InlineForm,
  ModalAnimation,
  Submit,
  Close
} from "./styles";
import {
  subscribeToTheCommunity,
  resetSubscription,
} from "../../../store/actions/community";
import { Center, Separator } from "../../../components/StyledComponents";
import { getFreeSubscriptions } from "../../../helpers/getFreeSubscriptions";
import { isSubscribed } from "../../../helpers/isSubscribed";
import closeIcon from "../../../assets/icons/close.svg";
import { User } from "../../../types";
import PhoneInput from "../../../components/FormikForm/PhoneInput";
import { Post } from "../../../types/post";
import ReactPixel from "react-facebook-pixel";

export interface ModalProps {
  showClose?: boolean
  animation?: string;
  width?: number;
  title?: string;
  onClose?: () => void;
  visibleModal?: string | null;
  ctaText?: string | "Suscribirme";
  avatar;
  group: string;
  username: string;
  idGroup: number;
  postId?: number;
  post?: Post;
  subscriptionStates?: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  handleSubmit?: ({ email, name, idGroup }: {
    email: string,
    name: string,
    idGroup: number,
    phone_number?: string,
    phone_area_code?: string
  }) => void
  isLoggedIn?: boolean;
  user?: User;
}

export const Component = ({
  showClose = false,
  animation = "fadeIn",
  width,
  title,
  onClose,
  visibleModal,
  ctaText,
  avatar,
  group,
  username,
  idGroup,
  postId,
  post,
  subscriptionStates,
  isLoggedIn,
  handleSubmit,
  user
}: ModalProps) => {
  const dispatch = useDispatch();
  const modalDOM = document.getElementById("modal") as HTMLElement;
  const [formSubmmited, setFormSubmmited] = useState<boolean>(false);
  const [subscriberEmail, setSubscriberEmail] = useState<string>("");
  const [subscriberName, setSubscriberName] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);

  // Verificar si la suscripción ya fue completada
  const isSubscriptionCompleted = (userId: number, postId: number): boolean => {
    const key = `lead_subscription_${userId}_${postId}`;
    return localStorage.getItem(key) === 'completed';
  };

  // Verificar si ya se completó la suscripción para este usuario y post
  const hasCompletedSubscription = useMemo(() => user?.id && postId ? isSubscriptionCompleted(user.id, postId) : false, [user, postId]);

  const trackLeadMagnetSubmission = useCallback((formValues) => {
    const leadId = post?.id ? String(post.id) : undefined;

    const eventPayload = {
      content_name: post?.title || title || "Lead Magnet",
      content_category: "Lead Magnet",
      content_ids: leadId ? [leadId] : [],
      contents: leadId
        ? [
          {
            id: leadId,
            quantity: 1,
          },
        ]
        : [],
      content_type: "lead",
      status: "form_submitted",
      post_id: post?.id,
      group_id: idGroup,
      group_name: group,
      lead_name: formValues.name,
      lead_email: formValues.email,
      lead_phone_number: formValues.phone_number,
      lead_area_code: formValues.phone_area_code,
      lead_terms_accepted: formValues.terminosCondiciones === "true",
    };

    try {
      ReactPixel.track("Lead", eventPayload);
      console.log("[Pixel] Lead enviado con:", eventPayload);
    } catch (error) {
      console.warn("[Pixel] Error al enviar Lead:", error);
    }
  }, [group, idGroup, post, title, username]);

  const onFormSubmit = ({ values, actions }) => {
    localStorage.setItem("KLOUSER_SUBSCRIBER_EMAIL", values.email);
    localStorage.setItem("KLOUSER_SUBSCRIBER_NAME", values.name);
    localStorage.setItem("KLOUSER_SUBSCRIBER_PHONE", values.phone_number);
    localStorage.setItem("KLOUSER_SUBSCRIBER_PHONE_AREA_CODE", values.phone_area_code);

    trackLeadMagnetSubmission(values);
    handleSubmit && handleSubmit({ ...values, idGroup })
  };

  // Marcar suscripción exitosa en localStorage
  const markSubscriptionAsCompleted = (userId: number, postId: number) => {
    const key = `lead_subscription_${userId}_${postId}`;
    localStorage.setItem(key, 'completed');
  };



  const handleClose = () => {
    dispatch(hideModal());
    setIsVisible(false);

    if (onClose) {
      onClose();
    }
  };

  const setBodyStyles = useCallback(() => {
    if (isVisible && !isSubscribed([], idGroup) && !hasCompletedSubscription) {
      document.body.style.height = "100%";
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.height = "auto";
      document.body.style.overflow = "auto";
    }
  }, [isVisible, idGroup, hasCompletedSubscription]);

  useEffect(() => {
    if (subscriptionStates?.success) {
      setIsVisible(false);
      dispatch(resetSubscription());

      // Marcar suscripción como completada si tenemos los IDs necesarios
      if (user?.id && postId) {
        markSubscriptionAsCompleted(user.id, postId);
      }
    }
  }, [subscriptionStates]);

  useEffect(() => {
    setIsVisible("ask-for-user-information" === visibleModal);
  }, [visibleModal]);

  useEffect(() => {
    setBodyStyles();
  }, [isVisible]);

  useEffect(() => {
    if (!isLoggedIn) {
      setSubscriberEmail(localStorage.getItem("KLOUSER_SUBSCRIBER_EMAIL") || "");
      setSubscriberName(localStorage.getItem("KLOUSER_SUBSCRIBER_NAME") || "");
    } else {
      setSubscriberEmail(user?.email || "");
      setSubscriberName(user?.name && user?.last_name ? `${user?.name} ${user?.last_name}` : "");
    }
  }, [isLoggedIn, user]);



  return ReactDOM.createPortal(
    <Transition
      in={isVisible && !isSubscribed([], idGroup) && !hasCompletedSubscription}
      timeout={350}
      mountOnEnter
    >
      {(state) => {
        return (
          <>
            <ModalAnimation name={animation} className={`animation-${state}`}>
              <Modal>
                <ModalDialog width={width}>
                  <ModalContent>
                    {showClose && (
                      <Close onClick={() => handleClose()}>
                        <img
                          src={closeIcon}
                          alt="X"
                          width="16px"
                          height="16px"
                        />
                      </Close>
                    )}
                    <ModalBody>
                      <ImageWrapper >
                        <Image src={avatar} withPhone={post?.ask_for_phone} />
                      </ImageWrapper>

                      <FormWrapper>
                        <Title>{title}</Title>

                        <Separator height={{ desktop: "10px", mobile: "10px" }} />

                        <SubTitle>
                          {post?.short_description || "Completa los datos para continuar"}
                        </SubTitle>

                        <Formik
                          initialValues={{
                            ...initialValues,
                            email: subscriberEmail,
                            name: subscriberName
                          }}
                          onSubmit={(values, actions) => {
                            if (onFormSubmit) onFormSubmit({ values, actions });
                          }}
                          validateOnChange={formSubmmited}
                          validateOnBlur={formSubmmited}
                          validationSchema={schema}
                          validate={(values) => {
                            const errors: any = {};

                            // Validación condicional para campos de teléfono
                            if (post?.ask_for_phone) {
                              if (!values.phone_number) {
                                errors.phone_number = "Ingresa tu número de teléfono";
                              }
                              if (!values.phone_area_code) {
                                errors.phone_area_code = "Selecciona el código de país";
                              }
                            }

                            return errors;
                          }}
                          enableReinitialize
                        >
                          {({
                            touched,
                            errors,
                            values,
                            handleChange,
                            isSubmitting,
                            handleBlur,
                            handleReset,
                            handleSubmit,
                            setFieldValue
                          }) => {
                            return (
                              <form
                                className="theme-form"
                                onSubmit={(event) => {
                                  setFormSubmmited(true);
                                  handleSubmit(event);
                                }}
                                id="subscribe-to-free-content"
                              >
                                <InlineForm>
                                  <div>
                                    <Input
                                      touched={touched["name"]}
                                      error={errors["name"]}
                                      type="text"
                                      name="name"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Ingresa tu primer nombre"
                                      options={{
                                        marginBottom: 20,
                                      }}
                                      value={values["name"]}
                                    />

                                    {post?.ask_for_phone && (
                                      <PhoneInput
                                        setFieldValue={setFieldValue}
                                        touched={touched}
                                        errors={errors}
                                        values={values}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        options={{
                                          marginBottom: 20,
                                        }}
                                      />
                                    )}


                                    <Input
                                      touched={touched["email"]}
                                      error={errors["email"]}
                                      type="text"
                                      name="email"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Ingresa tu email"
                                      options={{
                                        marginBottom: 20,
                                      }}
                                      value={values["email"]}
                                    />

                                    <Checkbox
                                      touched={touched["terminosCondiciones"]}
                                      error={errors["terminosCondiciones"]}
                                      name="terminosCondiciones"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values["terminosCondiciones"]}
                                      items={[
                                        {
                                          label:
                                            `Acepto recibir via correo electrónico contenido exclusivo, recursos útiles y consejos prácticos`,
                                          value: "true",
                                        },
                                      ]}
                                      options={{
                                        marginBottom: 24,
                                      }}
                                    />
                                  </div>

                                  <Submit
                                    isSubmmiting={!!subscriptionStates?.loading}
                                    form="subscribe-to-free-content"
                                  >
                                    {ctaText || "Suscribirme"}
                                  </Submit>

                                </InlineForm>
                              </form>
                            );
                          }}
                        </Formik>
                      </FormWrapper>
                    </ModalBody>
                  </ModalContent>
                </ModalDialog>
              </Modal>
            </ModalAnimation>
          </>
        );
      }}
    </Transition>,
    modalDOM
  );
};

const state = ({ modalStore, communityStore, userStore, postStore }) => {
  const { isLoggedIn } = userStore
  const { data: user } = userStore.information;
  const { states: subscriptionStates } = communityStore.subscription;
  const { data: post } = postStore.post;

  return { ...modalStore, subscriptionStates, isLoggedIn, user, post };
};

export default connect(state)(Component);
